import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import usePanelFlotante from "./usePanelFlotante";
import "./SearchSelect.css";

/**
 * Selector con busqueda.
 *
 * El panel se monta en <body> con posicion fija, no dentro del formulario.
 * Cuando estaba dentro, al abrirlo estiraba el contenedor scrolleable
 * (aparecia una barra de scroll de la nada) y ademas quedaba recortado por
 * su overflow. Con portal + posicion fija no puede pasar ninguna de las dos.
 *
 * Donde va el panel y cuando se cierra vive en usePanelFlotante, que
 * comparte con MultiSelect: es la parte con mas trampas y tenerla dos veces
 * significaria arreglar cada bug dos veces.
 */

function SearchSelect({
  label,
  name,
  value,
  onChange,
  disabled,
  options = [],
  placeholder = "Select...",
  searchPlaceholder = "Type to search...",
  maxListHeight = 260,
  allowClear = true,
}) {
  const {
    abierto: open,
    setAbierto: setOpen,
    triggerRef,
    panelRef,
    pos,
  } = usePanelFlotante({ altoLista: maxListHeight, extra: 62 });

  const [q, setQ] = useState("");
  const [activo, setActivo] = useState(-1);
  const listaRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value]
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return options;
    return options.filter((o) =>
      String(o.label || "").toLowerCase().includes(query)
    );
  }, [options, q]);

  // Calcula donde va el panel: debajo del control, o arriba si no hay lugar.
  // Al abrir, arranca sobre la opcion ya elegida.
  useEffect(() => {
    if (!open) {
      setQ("");
      setActivo(-1);
      return;
    }
    const i = filtered.findIndex((o) => String(o.value) === String(value));
    setActivo(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Mantiene visible la opcion resaltada al moverse con el teclado.
  useEffect(() => {
    if (!open || activo < 0 || !listaRef.current) return;
    const item = listaRef.current.children[activo];
    if (item?.scrollIntoView) item.scrollIntoView({ block: "nearest" });
  }, [activo, open]);

  const elegir = (opt) => {
    onChange?.({ target: { name, value: opt.value } });
    setOpen(false);
  };

  const limpiar = (e) => {
    e.stopPropagation();
    onChange?.({ target: { name, value: "" } });
    setOpen(false);
  };

  const alTeclear = (e) => {
    if (disabled) return;

    if (!open) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActivo((i) => (filtered.length ? (i + 1) % filtered.length : -1));
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActivo((i) =>
        filtered.length ? (i <= 0 ? filtered.length - 1 : i - 1) : -1
      );
      return;
    }

    if (e.key === "Home") {
      e.preventDefault();
      setActivo(filtered.length ? 0 : -1);
      return;
    }

    if (e.key === "End") {
      e.preventDefault();
      setActivo(filtered.length ? filtered.length - 1 : -1);
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (activo >= 0 && filtered[activo]) elegir(filtered[activo]);
    }
  };

  const panel =
    open && !disabled && pos
      ? createPortal(
          <div
            ref={panelRef}
            className="ss-panel"
            style={{
              left: pos.left,
              width: pos.width,
              ...(pos.top !== null ? { top: pos.top } : { bottom: pos.bottom }),
              maxHeight: pos.maxAlto,
            }}
            role="listbox"
          >
            <div className="ss-buscador">
              <span className="ss-buscador-ic" aria-hidden="true">
                &#9906;
              </span>
              <input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setActivo(0);
                }}
                onKeyDown={alTeclear}
                placeholder={searchPlaceholder}
                className="ss-buscador-input"
                autoFocus
              />
              {q ? (
                <button
                  type="button"
                  className="ss-buscador-x"
                  onClick={() => setQ("")}
                  aria-label="Clear search"
                >
                  &#10005;
                </button>
              ) : null}
            </div>

            <div className="ss-lista" ref={listaRef}>
              {filtered.length === 0 ? (
                <div className="ss-sin-resultados">
                  No matches for &ldquo;{q.trim()}&rdquo;
                </div>
              ) : (
                filtered.map((opt, i) => {
                  const elegida = String(opt.value) === String(value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onMouseEnter={() => setActivo(i)}
                      onClick={() => elegir(opt)}
                      className={`ss-item ${elegida ? "ss-item--elegida" : ""} ${
                        i === activo ? "ss-item--activa" : ""
                      }`}
                      role="option"
                      aria-selected={elegida}
                      title={opt.label}
                    >
                      <span className="ss-item-texto">{opt.label}</span>
                      {elegida ? (
                        <span className="ss-item-check" aria-hidden="true">
                          &#10003;
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>

            <div className="ss-pie">
              {filtered.length} of {options.length}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="ss-grupo">
      {label ? <label className="ss-label">{label}</label> : null}

      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((s) => !s)}
        onKeyDown={alTeclear}
        className={`ss-trigger ${open ? "ss-trigger--abierto" : ""} ${
          disabled ? "ss-trigger--off" : ""
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected ? "ss-valor" : "ss-placeholder"}>
          {selected ? selected.label : placeholder}
        </span>

        <span className="ss-acciones">
          {allowClear && selected && !disabled ? (
            <span
              className="ss-limpiar"
              onMouseDown={limpiar}
              role="button"
              tabIndex={-1}
              aria-label={`Clear ${label || name}`}
              title="Clear"
            >
              &#10005;
            </span>
          ) : null}
          <span className="ss-flecha" aria-hidden="true">
            &#9662;
          </span>
        </span>
      </button>

      {panel}
    </div>
  );
}

export default SearchSelect;
